import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { 
  GraduationCap, Save, Eye, EyeOff, Palette, 
  Loader2, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ConfigBannerSemestre = () => {
  const [config, setConfig] = useState({
    activo: true,
    titulo: 'Clases Próximo Semestre',
    color_fondo: '#3b82f6',
    color_texto: '#ffffff'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clases, setClases] = useState([]);
  const [clasesLoading, setClasesLoading] = useState(true);

  useEffect(() => {
    loadConfig();
    loadClases();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/banner-semestre`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadClases = async () => {
    try {
      setClasesLoading(true);
      const response = await axios.get(`${API}/clases`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      // Filtrar solo clases de estudiantes
      const clasesEstudiantes = response.data.filter(c => c.categoria === 'estudiantes');
      setClases(clasesEstudiantes);
    } catch (error) {
      console.error('Error loading clases:', error);
      toast.error('Error al cargar clases');
    } finally {
      setClasesLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/banner-semestre`, config, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      toast.success('Banner actualizado correctamente');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const toggleClaseActiva = async (claseId, currentValue) => {
    // currentValue = clase.mostrar_en_banner_semestre actual (true por defecto si no existe)
    const nuevoValor = !currentValue;
    try {
      await axios.put(
        `${API}/admin/banner-semestre/clase/${claseId}/visibilidad`,
        { mostrar: nuevoValor },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
      );
      setClases(prev => prev.map(c => 
        c.id === claseId ? { ...c, mostrar_en_banner_semestre: nuevoValor } : c
      ));
      toast.success(`Clase ${nuevoValor ? 'añadida al' : 'removida del'} banner`);
    } catch (error) {
      console.error('Error updating clase banner visibility:', error);
      toast.error('Error al actualizar visibilidad en banner');
    }
  };

  const predefinedColors = [
    { name: 'Azul', value: '#3b82f6' },
    { name: 'Índigo', value: '#6366f1' },
    { name: 'Verde', value: '#22c55e' },
    { name: 'Cian', value: '#06b6d4' },
    { name: 'Violeta', value: '#8b5cf6' },
    { name: 'Azul Marino', value: '#1e40af' }
  ];

  // Contar clases visibles en banner (default true para legacy)
  const isVisibleEnBanner = (c) => c.mostrar_en_banner_semestre !== false;
  const clasesActivas = clases.filter(isVisibleEnBanner);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-blue-600" />
            Banner Clases del Semestre
          </h1>
          <p className="text-gray-500 mt-1">
            Configura el carrusel de clases del próximo semestre en la página de inicio
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Guardar Cambios
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuración del Banner */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-blue-600" />
              Configuración del Banner
            </CardTitle>
            <CardDescription>
              Activa/desactiva y personaliza el color del banner
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Switch Activo */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
              <div className="flex items-center gap-3">
                {config.activo ? (
                  <Eye className="w-5 h-5 text-blue-600" />
                ) : (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                )}
                <div>
                  <Label className="font-medium">Estado del Banner</Label>
                  <p className="text-sm text-gray-500">
                    {config.activo ? 'Visible en la página de inicio' : 'Oculto para visitantes'}
                  </p>
                </div>
              </div>
              <Switch
                checked={config.activo}
                onCheckedChange={(checked) => setConfig({ ...config, activo: checked })}
              />
            </div>

            {/* Selector de Color */}
            <div className="space-y-3">
              <Label>Color de Fondo</Label>
              <div className="grid grid-cols-3 gap-2">
                {predefinedColors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setConfig({ ...config, color_fondo: color.value })}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                      config.color_fondo === color.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div
                      className="w-6 h-6 rounded-full border border-gray-300"
                      style={{ backgroundColor: color.value }}
                    />
                    <span className="text-sm font-medium">{color.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Vista Previa */}
            <div className="space-y-2">
              <Label>Vista Previa</Label>
              <div 
                className="rounded-xl shadow-lg overflow-hidden"
                style={{ background: `linear-gradient(to right, ${config.color_fondo}, ${config.color_fondo}dd)` }}
              >
                <div className="flex items-center justify-between px-4 py-2 bg-black/10">
                  <div className="flex items-center gap-2 text-white">
                    <BookOpen className="w-4 h-4" />
                    <span className="text-sm font-medium">Clases Próximo Semestre</span>
                  </div>
                  <div className="flex gap-1.5">
                    {[0, 1, 2].map((idx) => (
                      <div
                        key={idx}
                        className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-white' : 'bg-white/40'}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="px-4 py-4">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-16 rounded-lg bg-white/20 flex items-center justify-center">
                      <GraduationCap className="w-8 h-8 text-white/60" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-base">Nombre de la Clase</h3>
                      <p className="text-white/70 text-xs mt-1">Descripción breve de la clase...</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-6 h-6 rounded-full bg-white/20" />
                        <span className="text-white/80 text-xs">Profesor Nombre</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Solo aparecen en el banner las clases de estudiantes
                que estén activas, pertenezcan al semestre actual y estén marcadas como visibles.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Selección de Clases */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              Clases del Semestre
            </CardTitle>
            <CardDescription>
              Selecciona las clases que aparecerán en el banner del semestre
            </CardDescription>
          </CardHeader>
          <CardContent>
            {clasesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : clases.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay clases de estudiantes</p>
                <p className="text-sm">Crea algunas clases primero</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {/* Resumen */}
                <div className="p-3 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg mb-4">
                  <p className="text-sm font-medium text-blue-800">
                    {clasesActivas.length} de {clases.length} clases visibles en el banner
                  </p>
                </div>

                {clases.map((clase) => {
                  const visible = isVisibleEnBanner(clase);
                  return (
                  <div 
                    key={clase.id} 
                    className={`p-4 rounded-lg border-2 transition-all ${
                      visible
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex gap-3">
                      {/* Imagen de la clase */}
                      <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        {(clase.banner_url || clase.imagen_url) ? (
                          <img
                            src={clase.banner_url || clase.imagen_url}
                            alt={clase.titulo}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <GraduationCap className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info de la clase */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 truncate">{clase.titulo}</h4>
                            <p className="text-sm text-gray-500 truncate">
                              {clase.profesor_nombre || 'Sin profesor asignado'}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {visible ? '✅ Visible en banner' : '⏸️ Oculta del banner'}
                            </p>
                          </div>
                          <Switch
                            data-testid={`banner-toggle-${clase.id}`}
                            checked={visible}
                            onCheckedChange={() => toggleClaseActiva(clase.id, visible)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConfigBannerSemestre;
